import { ReactNode, useState, useMemo, useEffect, type CSSProperties } from 'react';
import { TopNav } from './top-nav';
import { Sidebar } from './sidebar';
import { MobileNav } from './mobile-nav';
import { LayoutContext } from './layout-context';
import { useAuth } from '@/lib/auth/auth-context';
import { useRouter, usePathname } from '@/lib/compat/navigation';
import { ShieldBan, X } from 'lucide-react';
import Link from '@/lib/compat/link';
import { cn } from '@/lib/utils';
import { moreMenuItems, type NavSection } from '@/config/navigation';
import { useTranslations } from '@/lib/i18n/client';
import { useI18nSafe } from '@/lib/i18n/i18n-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { getRequiredPermissionForRoute } from '@/lib/constants/permissions';
import { AccessDenied } from '@/components/shared/access-denied';
import { EmptyState } from '@/components/shared/empty-state';
import { Button } from '@/components/ui/button';
import { ImpersonationBanner } from './impersonation-banner';
import { isLaunchBlockedRoute } from '@/lib/capabilities/launch-route-policy';
import { useFilteredNavSections } from '@/lib/hooks/use-filtered-nav-sections';

interface DashboardLayoutProps {
  children: ReactNode;
  sidebarSections?: NavSection[];
}

export function DashboardLayout({ children, sidebarSections }: DashboardLayoutProps) {
  const {
    user,
    loading,
    activeChannel,
    channelSelectionResolved,
    needsChannelSelection,
    logout,
  } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const t = useTranslations();
  const { language } = useI18nSafe();
  const { hasPermission } = usePermissions();
  const layoutDir = language === 'ar' ? 'rtl' : 'ltr';

  // The owning route passes its sidebar explicitly. Deriving it from the
  // router's target pathname makes the new sidebar appear beside stale page
  // content while a lazy route is still loading.
  const activeSidebar = sidebarSections;
  const visibleSidebar = useFilteredNavSections(activeSidebar);
  const subNavItems = useMemo(
    () => visibleSidebar?.flatMap((s) => s.items) || [],
    [visibleSidebar]
  );

  const layoutContextValue = useMemo(() => ({
    hasSidebar: !!visibleSidebar,
    subNavItems,
    pathname,
  }), [visibleSidebar, subNavItems, pathname]);

  // Redirect to login if not authenticated.
  // Important: if a stored JWT exists but `user` is null, auth-context's
  // refreshAuth() hit a transient network error (backend down, CORS, timeout)
  // and preserved the token. In that case we must NOT redirect — staying on
  // the current page lets auth auto-recover when the backend is reachable
  // again. We only redirect when there's no stored token at all, i.e. the
  // user is genuinely logged out.
  useEffect(() => {
    if (!loading && !user) {
      const hasStoredToken = typeof window !== 'undefined'
        ? !!sessionStorage.getItem('kayn_auth_token')
        : false;
      if (!hasStoredToken) {
        router.push('/login');
      }
    }
  }, [user, loading, router]);

  // Wait until auth-context has explicitly finished restoring or rejecting the
  // persisted store selection. Relying on `loading` or `channelSelectionResolved`
  // alone is still racy on refresh: persisted store markers can exist for one
  // render before `activeChannel` is rehydrated, which would trigger a false
  // redirect to /select-store. We keep waiting while any persisted selection is
  // present, and auth-context clears those keys when it proves they are stale.
  const hasPersistedChannelSelection = typeof window !== 'undefined' && (
    !!localStorage.getItem('kayn_active_channel_id') ||
    !!localStorage.getItem('kayn_active_channel_code') ||
    !!localStorage.getItem('kayn_active_channel') ||
    !!localStorage.getItem('kayn_channel_token')
  );
  const isWaitingForChannelHydration =
    !!user && !activeChannel && hasPersistedChannelSelection;

  // Redirect to store selection only after channel restoration has definitively
  // completed and no active channel could be resolved.
  useEffect(() => {
    if (!loading && user && channelSelectionResolved && needsChannelSelection && !isWaitingForChannelHydration) {
      router.replace('/select-store');
    }
  }, [user, loading, channelSelectionResolved, needsChannelSelection, isWaitingForChannelHydration, router]);

  // Merchant profile completion is intentionally non-blocking. Migrated stores
  // must retain dashboard access while operators finish profile and launch setup.

  // Route-level permission check
  const requiredPermission = getRequiredPermissionForRoute(pathname);
  const routeAccessDenied = !!requiredPermission && !hasPermission(requiredPermission);
  const routeCapabilityUnavailable = isLaunchBlockedRoute(pathname);

  // Filter more menu items by permission
  const visibleMoreMenuItems = moreMenuItems.filter(
    (i) => !i.requiredPermission || hasPermission(i.requiredPermission)
  );

  // Close drawers when route changes
  useEffect(() => {
    setMoreMenuOpen(false);
  }, [pathname]);

  // isWaitingForChannelHydration already computed above (before the redirect guard)

  if (loading || isWaitingForChannelHydration) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Channel selection happens via a useEffect redirect above.
  if (needsChannelSelection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
        <div className="max-w-md w-full text-center space-y-4 bg-card text-card-foreground rounded-2xl  p-8">
          <h2 className="text-xl font-semibold">Opening store selection</h2>
          <p className="text-sm text-muted-foreground">
            Choose the store you want to manage to continue.
          </p>
          <button
            type="button"
            onClick={() => router.replace('/select-store')}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium"
          >
            Open store selection
          </button>
        </div>
      </div>
    );
  }

  // A transient auth/bootstrap failure should never present itself as a lost
  // session. AuthProvider keeps retrying in the background, so keep the state
  // neutral and stable until it recovers or proves the user is logged out.
  if (!user) {
    const hasStoredToken = typeof window !== 'undefined'
      ? !!sessionStorage.getItem('kayn_auth_token')
      : false;

    if (hasStoredToken) {
      return (
        <div dir={layoutDir} className="flex min-h-screen items-center justify-center bg-background">
          <div className="size-7 animate-spin rounded-full border-2 border-border border-t-primary" aria-label="Loading" />
        </div>
      );
    }

    // Genuinely logged out — the useEffect above is redirecting to /login.
    return null;
  }

  // Extra safety: don't render if no active channel (shouldn't happen but prevents data leaks)
  if (!activeChannel) {
    return null;
  }

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <div
        className="min-h-screen bg-background"
        dir={layoutDir}
        style={{
          '--dashboard-sidebar-width': visibleSidebar ? '15rem' : '0rem',
        } as CSSProperties}
      >
        {/* Impersonation banner (PADMIN.26) — shown when platform ops views a merchant store */}
        <ImpersonationBanner />

        {/* Desktop top navigation - hidden on mobile, scrolls with content */}
        <div className="hidden lg:block">
          <TopNav />
        </div>

        <div className="flex lg:gap-0">
          {/* Desktop sidebar - hidden on mobile, sticky below top nav */}
          {visibleSidebar && (
            <div className="sticky top-[3.75rem] hidden h-[calc(100dvh-3.75rem)] shrink-0 border-e border-border/80 bg-card py-5 ps-4 lg:block">
              <Sidebar sections={visibleSidebar} />
            </div>
          )}

          {/* Main content area */}
          <main className={cn(
            'flex-1 min-w-0 w-full overflow-x-clip',
            // Mobile: full width with bottom padding for nav
            'pb-24 lg:pb-0',
            // Desktop: padding around content card
            'lg:px-5 lg:py-5 xl:px-7'
          )}>
            <div
              className={cn(
                'mx-auto w-full lg:min-h-[calc(100dvh-6.25rem)]',
                visibleSidebar ? 'max-w-[1480px]' : 'max-w-[1280px]',
              )}
            >
              <div className="w-full">
              {routeAccessDenied ? (
                <AccessDenied />
              ) : routeCapabilityUnavailable ? (
                <div className="mx-auto flex min-h-[55vh] max-w-2xl items-center px-4">
                  <EmptyState
                    icon={<ShieldBan className="size-5" />}
                    title={t('capabilities.unavailableTitle')}
                    description={t('capabilities.unavailableDescription')}
                    action={(
                      <Button asChild variant="outline" size="sm">
                        <Link href="/capabilities">{t('capabilities.reviewAll')}</Link>
                      </Button>
                    )}
                  />
                </div>
              ) : children}
              </div>
            </div>
          </main>
        </div>

        {/* Mobile bottom navigation */}
        <MobileNav onMoreClick={() => setMoreMenuOpen(true)} />

        {/* More menu drawer for mobile */}
        {moreMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMoreMenuOpen(false)}
            />

            {/* Drawer */}
            <div className="absolute inset-y-0 end-0 flex w-72 animate-in flex-col bg-background duration-300 ltr:slide-in-from-right rtl:slide-in-from-left">
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b bg-background shrink-0">
                <h2 className="text-lg font-semibold">{t('common.more')}</h2>
                <button
                  onClick={() => setMoreMenuOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Menu items - from centralized config */}
              <nav className="flex-1 overflow-y-auto p-2">
                {visibleMoreMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{t(item.labelKey)}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Logout button */}
              <div className="sticky bottom-0 inset-x-0 p-4 border-t bg-background shrink-0">
                <button
                  onClick={() => {
                    logout();
                    setMoreMenuOpen(false);
                  }}
                  className="w-full py-3 text-center text-primary font-medium hover:bg-primary/10 rounded-lg transition-colors"
                >
                  {t('common.logout')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutContext.Provider>
  );
}
